import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { v4 as uuid } from 'uuid';
import { Subscription, tap, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { DialogRef, ToastService, ToastType } from '@hglobal/common';
import { TranslateService } from '@ngx-translate/core';
import { tapResponse } from '@ngrx/operators';
import {
  AiChatContext,
  ChatLanguage,
  ChatListItem,
  ChatMessageReaction,
  ChatMessageRole,
  ChatMessage,
  CreateChatMessageReq,
  InternalMessageStatus,
  LeaveFeedbackReq,
  MessageRating,
  SubmitFeedbackReq,
  ToggleReactionReq,
  WorkContextOption,
} from '@fb/models';

import { AiChatFormService } from '../ai-chat-form.service';
import { ChatStreamFailure } from '../helpers/chat-stream-failure';
import {
  isWorkContextOption,
  workContextChatFields,
  workContextRequest,
} from '../helpers/work-context';
import {
  AI_CHAT_DEFAULT_GREETING_LANG,
  AI_CHAT_SEND_PENDING_TIMEOUT_MS,
  AI_CHAT_DEFAULT_CONTEXT,
  AI_CHATS_PAGE_SIZE,
  DEFAULT_CHAT_TITLE,
} from '../ai-chat.constants';
import { AiChatTabBridgeService } from '../ai-chat-tab-bridge.service';
import { AiChatWorkContextPickerDialog } from '../ai-chat-work-context-picker.dialog';

@Injectable()
export class AiChatStore {
  readonly #service = inject(AiChatFormService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #toast = inject(ToastService);
  readonly #translate = inject(TranslateService);
  readonly #workContextPickerDialog = inject(AiChatWorkContextPickerDialog);
  readonly #tabBridge = inject(AiChatTabBridgeService, { optional: true });

  readonly #feedbackMessageId = signal('');
  readonly #historySidebarCollapsed = signal(false);
  readonly feedbackMessageId = this.#feedbackMessageId.asReadonly();
  readonly historySidebarCollapsed = this.#historySidebarCollapsed.asReadonly();

  /**
   * Messages for every chat touched this session, keyed by chatId (or by
   * #draftId while chatId is still null). Switching chats never clears
   * this, so a switch is instant and a reply/error that arrives after the
   * user has navigated away still lands in the chat that sent it instead of
   * whatever chat is on screen when it arrives.
   */
  readonly #messagesCache = signal<Record<string, ChatMessage[]>>({});
  readonly #chatId = signal<string | null>(null);
  /** Unique id for the current "new chat" draft — the cache key until the server assigns a real chatId. */
  readonly #draftId = signal<string>(uuid());

  readonly #chats = signal<ChatListItem[]>([]);
  readonly #selectedChatId = signal<string | null>(null);
  readonly #chatsPage = signal(1);
  readonly #chatsHasMore = signal(false);

  // ── v2 chats list resource ──────────────────────────────────────────────
  /**
   * Drives #chatsRes; undefined = idle (resource stays in idle state, no auto-load on init).
   * Serial ensures every loadChats() call triggers a fresh fetch even for the same page/reset.
   */
  readonly #chatsPageReq =
    signal<{ page: number; reset: boolean; serial: number; context?: AiChatContext } | undefined>(
      undefined,
    );
  #chatsSerial = 0;

  readonly #chatsRes = rxResource({
    request: () => this.#chatsPageReq(),
    loader: ({ request }) => this.#service.getChats(AI_CHATS_PAGE_SIZE, request.page, { context: request.context }),
  });

  // ── select-chat resource ────────────────────────────────────────────────
  /**
   * Separate from #selectedChatId: only set when the user explicitly selects a chat
   * or clears selection. NOT updated on sendMessage success to avoid an unwanted re-fetch
   * that would overwrite optimistic messages and surface spurious CORS errors.
   * undefined = idle (rxResource stays idle, no error state).
   */
  readonly #selectedChatFetchId = signal<string | undefined>(undefined);

  readonly #selectedChatRes = rxResource({
    // selectChat never sets this to a client draft key or to the chat currently generating.
    request: () => this.#selectedChatFetchId(),
    loader: ({ request }) => this.#service.getChat(request),
  });

  // ── rename-chat mutation resource ───────────────────────────────────────
  /** undefined = idle; rxResource stays idle until renameChat() provides a request. */
  readonly #renameChatReq = signal<{ chatId: string; title: string } | undefined>(undefined);
  #renameChatRollback: { chatId: string; prevTitle: string } | null = null;

  readonly #renameChatRes = rxResource({
    request: () => this.#renameChatReq(),
    loader: ({ request }) => this.#service.renameChat(request.chatId, request.title),
  });

  // ── delete-chat mutation resource ───────────────────────────────────────
  /** undefined = idle; rxResource stays idle until deleteChat() provides a chatId. */
  readonly #deleteChatIdReq = signal<string | undefined>(undefined);
  /** Snapshot held for optimistic-delete rollback on API error. */
  #deleteChatRollback: ChatListItem | null = null;

  readonly #deleteChatRes = rxResource({
    request: () => this.#deleteChatIdReq(),
    loader: ({ request }) => this.#service.deleteChat(request),
  });

  // ── pin-chat mutation resource ───────────────────────────────────────────
  /** undefined = idle; rxResource stays idle until pinChat() provides a request. */
  readonly #pinChatReq = signal<{ chatId: string; isPinned: boolean } | undefined>(undefined);
  #pinChatRollback: { chatId: string; prevIsPinned: boolean } | null = null;

  readonly #pinChatRes = rxResource({
    request: () => this.#pinChatReq(),
    loader: ({ request }) => this.#service.pinChat(request.chatId, request.isPinned),
  });

  /**
   * In-flight or successfully titled chats. Removed on failure so the next
   * successful send can retry. Per-chat subscribe (not rxResource) so one
   * title call does not abort another.
   */
  readonly #titleRequestedChatIds = new Set<string>();
  /** Survives list reloads so a late GET cannot wipe a title the backend already wrote. */
  readonly #generatedTitles = new Map<string, string>();

  // ── send-message mutation resource ─────────────────────────────────────
  /** undefined = idle; rxResource stays idle until sendMessage() provides a request. */
  readonly #sendMessageReq = signal<SendMessageRequest | undefined>(undefined);

  readonly #sendMessageRes = rxResource({
    request: () => this.#sendMessageReq(),
    loader: ({ request }) => this.#service.createChatMessage(request.req),
  });

  /**
   * Rollback for the send that is currently awaiting a reply. Closing the chat
   * before the reply arrives cancels the request (the per-dialog store is
   * destroyed and the resource is torn down), so neither the success nor the
   * error effect fires. We invoke this on destroy to undo the optimistic CTA
   * cooldown — a cancelled request is not a success, so the CTA must reappear.
   */
  #inFlightSendOnError: (() => void) | null = null;
  /** originKey -> resolved chatId, for the current send only; cleared when the next send starts. */
  #resolvedSendKeys: Record<string, string> = {};
  /** originKey of the send owning #inFlightSendKey — never rewritten, unlike the cache key it may resolve to. */
  #inFlightSendOrigin: string | null = null;
  /**
   * Cache keys of optimistic rows with no server chat yet — not fetchable, renameable, pinnable,
   * or deletable — mapped to the For Work option their first message was sent with, restored when
   * the row is reselected.
   */
  readonly #clientDraftKeys = new Map<string, WorkContextOption | null>();
  /** Cache key of the send currently awaiting its terminal result (final reply or error); null when idle. Distinct from #sendMessageRes.isLoading(), which clears after the FIRST streaming chunk, not at completion. */
  readonly #inFlightSendKey = signal<string | null>(null);
  /** Bumped on every write to a cache key, so a GET that outlived a send racing it can detect it is now stale. */
  #messagesCacheVersion: Record<string, number> = {};
  /** Cache-key version captured at selectChat — lets the apply effect detect writes after the user selected, including during a skipped-then-started GET. */
  #selectedChatFetchVersion: Record<string, number> = {};
  // ─────────────────────────────────────────────────────────────────────────
  /** Cache key for whatever chat is currently on screen. */
  readonly #viewKey = computed(() => this.#chatId() ?? this.#draftId());
  readonly messages = computed(() => this.#messagesCache()[this.#viewKey()] ?? []);
  readonly chats = this.#chats.asReadonly();
  /** Id of the chat currently generating a reply (draft id until resolved, then the real chatId) — null when idle. Drives the sidebar's "generating" indicator; stays in sync with `#chats` entry ids through `#resolveSendKey`. */
  readonly generatingChatId = this.#inFlightSendKey.asReadonly();
  readonly selectedChatId = this.#selectedChatId.asReadonly();
  readonly chatsHasMore = this.#chatsHasMore.asReadonly();
  readonly chatsLoading = computed(() => this.#chatsRes.isLoading());
  /** True only when loading additional pages (scroll-triggered pagination), not on full list refreshes. */
  readonly chatsLoadingMore = computed(() => {
    const req = this.#chatsPageReq();

    return this.#chatsRes.isLoading() && req?.reset === false;
  });
  readonly chatsError = computed(() => this.#chatsRes.error() ?? null);
  /** Suppressed once the cache already has content for this chat, so a background refetch does not blank a switch that already rendered instantly. */
  readonly chatLoading = computed(() => this.#selectedChatRes.isLoading() && this.messages().length === 0);
  readonly error = computed(() => {
    const chatError = this.#selectedChatRes.error();

    // Only for the chat on screen, and not while it's still generating — that GET
    // can race backend persistence and 404 transiently; the send's own handler
    // owns that chat's state, not this GET.
    if (
      chatError
      && this.#selectedChatFetchId() === this.#viewKey()
      && this.#inFlightSendKey() !== this.#viewKey()
    ) {
      return chatError;
    }

    const req = this.#sendMessageReq();
    const sendError = this.#sendMessageRes.error();

    // A send's error belongs to the chat it was sent from — surface it only
    // while that chat is still the one on screen, so an error for a chat the
    // user has since switched away from does not show up over an unrelated one.
    if (!req || !sendError || this.#sendKeyFor(req) !== this.#viewKey()) {
      return null;
    }

    return sendError;
  });
  // ─────────────────────────────────────────────────────────────────────────

  readonly isLoading = computed(() => this.#sendMessageRes.isLoading());

  readonly disableSendQuestion = computed(() => (
    this.#inFlightSendKey() !== null
    || this.messages().some(message => message.isStreaming === true)
  ));

  /**
   * For Work option picked for the draft (new) chat; sent as `workContext` (without its `label`)
   * with its first message.
   */
  readonly #draftWorkContext = signal<WorkContextOption | null>(null);

  #workContextPickerRef: DialogRef | null = null;
  #workContextPickerSub: Subscription | null = null;
  /**
   * Message held until the picker closes, with its CTA cooldown rollback and the sender's hook
   * that takes the text back if the picker is dismissed.
   */
  #pendingContent: string | null = null;
  #pendingOnError?: () => void;
  #pendingOnPickerCancel?: (content: string) => void;
  readonly #isWorkContextPickerOpen = signal(false);
  readonly isWorkContextPickerOpen = this.#isWorkContextPickerOpen.asReadonly();

  constructor() {
    // Chat closed before the bot replied: the in-flight send is cancelled with
    // no success/error effect, so roll back the optimistic CTA cooldown here.
    this.#destroyRef.onDestroy(() => {
      this.#inFlightSendOnError?.();
      this.#inFlightSendOnError = null;
      this.#pendingOnError?.();
      this.#pendingOnError = undefined;
    });

    // ── #chatsRes: accumulate pages into #chats ────────────────────────
    effect(() => {
      const res = this.#chatsRes.value();

      if (!res) {
        return;
      }

      const { page, reset } = untracked(() => this.#chatsPageReq());

      untracked(() => {
        this.#chats.update(existing => sortChats(
          reset
            ? this.#mergeResetChatsList(existing, res.items)
            : [...existing, ...res.items.map(chat => this.#withGeneratedTitle(chat))],
        ));
        this.#chatsPage.set(page + 1);
        this.#chatsHasMore.set(page < res.pages);
      });
    }, { allowSignalWrites: true });

    // ── #selectedChatRes: sync messages when a chat is loaded ──────────────
    effect(() => {
      const chat = this.#selectedChatRes.value();
      const fetchId = this.#selectedChatFetchId();

      if (!fetchId || !chat) {
        return;
      }

      untracked(() => {
        // A send for this chat is still awaiting its terminal result — the GET
        // cannot have that turn yet, so applying it now would erase the
        // optimistic bubbles. Skip; the send's own success/error handler will
        // settle it and clear #inFlightSendKey when it does.
        if (this.#inFlightSendKey() === fetchId) {
          return;
        }

        // This GET's snapshot predates a send that has since written to the same
        // key (e.g. it finished while this GET was still in flight) — applying it
        // now would blow away the newer, authoritative state. Skip.
        if ((this.#messagesCacheVersion[fetchId] ?? 0) !== (this.#selectedChatFetchVersion[fetchId] ?? 0)) {
          return;
        }

        const cachedMessages = this.#messagesCache()[fetchId] ?? [];
        const serverMessages = chat.messages;
        const serverIds = new Set(serverMessages.map(message => message.id));

        // Don't replace the cache if it has a server-issued row this snapshot is missing
        // (persistence lag); client-only rows are expected to be absent server-side.
        if (cachedMessages.some(message => !message.isClientOnly && !serverIds.has(message.id))) {
          return;
        }

        // Mirror ChatBot 1.0: answers loaded from history (a reopened chat) do not
        // accept feedback — only answers produced live in the current session do.
        this.#setMessagesCacheForKey(
          fetchId,
          [...serverMessages]
            .reverse()
            .map(message => ({ ...message, isFeedbackEligible: false })),
        );
      });
    }, { allowSignalWrites: true });

    // ── #renameChatRes: apply server response ──────────────────────────────
    effect(() => {
      const updated = this.#renameChatRes.value();

      if (!updated) {
        return;
      }

      untracked(() => {
        this.#chats.update(chats => sortChats(chats.map(chat => chat.id === updated.id ? { ...chat, ...updated } : chat)));
      });
    }, { allowSignalWrites: true });

    // ── #renameChatRes: rollback on error ──────────────────────────────────
    effect(() => {
      if (!this.#renameChatRes.error()) {
        return;
      }

      untracked(() => {
        const rollback = this.#renameChatRollback;

        if (rollback) {
          this.#chats.update(chats => chats.map(chat => chat.id === rollback.chatId ? { ...chat, title: rollback.prevTitle } : chat));
          this.#renameChatRollback = null;
        }

        this.#toast.show({ type: ToastType.Error, message: this.#translate.instant('chatBot.history.renameError') });
      });
    }, { allowSignalWrites: true });

    // ── #deleteChatRes: rollback on error ──────────────────────────────────
    effect(() => {
      if (this.#deleteChatRes.error()) {
        untracked(() => {
          const snapshot = this.#deleteChatRollback;

          if (snapshot) {
            this.#chats.update(chats => {
              const alreadyExists = chats.some(c => c.id === snapshot.id);

              return alreadyExists ? chats : sortChats([...chats, snapshot]);
            });
            this.#deleteChatRollback = null;
          }

          this.#toast.show({ type: ToastType.Error, message: this.#translate.instant('chatBot.history.deleteError') });
        });
      }
    }, { allowSignalWrites: true });

    // ── #pinChatRes: apply server response ────────────────────────────────
    effect(() => {
      const updated = this.#pinChatRes.value();

      if (!updated) {
        return;
      }

      untracked(() => {
        this.#chats.update(chats => sortChats(chats.map(chat => chat.id === updated.id ? { ...chat, ...updated } : chat)));
      });
    }, { allowSignalWrites: true });

    // ── #pinChatRes: rollback on error ─────────────────────────────────────
    effect(() => {
      if (!this.#pinChatRes.error()) {
        return;
      }

      untracked(() => {
        const rollback = this.#pinChatRollback;

        if (rollback) {
          this.#chats.update(chats => sortChats(
            chats.map(chat => chat.id === rollback.chatId ? { ...chat, isPinned: rollback.prevIsPinned } : chat),
          ));
          this.#pinChatRollback = null;
        }

        this.#toast.show({ type: ToastType.Error, message: this.#translate.instant('chatBot.history.pinError') });
      });
    }, { allowSignalWrites: true });

    // ── #sendMessageRes: apply streaming snapshots + final agent reply ────
    effect(() => {
      const req = this.#sendMessageReq();
      const agentMsg = this.#sendMessageRes.value();

      if (!req || !agentMsg) {
        return;
      }

      untracked(() => {
        if (!agentMsg.isStreaming) {
          this.#applyFinalAgentReply(req, agentMsg);

          return;
        }

        this.#applyStreamingAgentSnapshot(req, agentMsg);
      });
    }, { allowSignalWrites: true });

    // ── #sendMessageRes: remove optimistic / keep partial on stream error ─
    effect(() => {
      const req = this.#sendMessageReq();

      if (!req || !this.#sendMessageRes.error()) {
        return;
      }

      untracked(() => {
        const streamError = this.#sendMessageRes.error();
        const hadContent = this.#shouldKeepPartialOnStreamError(req, streamError);

        if (!hadContent) {
          this.#removeOptimisticSendMessages(req);
        } else {
          this.#keepPartialStreamOnError(req);
        }

        // The optimistic sidebar entry (added in #executeSend) only earns its
        // spot once the server actually creates the chat (#resolveSendKey runs).
        // If that never happened, drop it instead of leaving a blank ghost chat
        // that 404s when clicked.
        if (req.req.chatId === null && !this.#resolvedSendKeys[req.originKey]) {
          this.#chats.update(chats => chats.filter(chat => chat.id !== req.originKey));

          // Don't leave the selection pointing at a row that no longer exists.
          if (this.#selectedChatId() === req.originKey) {
            this.#selectedChatId.set(null);
          }
        }

        // Error effect owns the rollback; clear so destroy does not call it twice.
        this.#inFlightSendOnError = null;
        req.onError?.();
        this.#settleSend(req);
      });
    }, { allowSignalWrites: true });

    const bridge = this.#tabBridge;

    if (bridge) {
      let previousContext: AiChatContext | undefined;

      effect(() => {
        const context = bridge.activeContext();

        if (previousContext === undefined) {
          previousContext = context;

          return;
        }

        if (context === previousContext) {
          return;
        }

        previousContext = context;

        untracked(() => {
          this.startNewChat();
          this.loadChats();
        });
      }, { allowSignalWrites: true });
    }

    this.#destroyRef.onDestroy(() => {
      this.#closeWorkContextPickerIfOpen();
    });
  }


  #closeWorkContextPickerIfOpen(): void {
    this.#workContextPickerSub?.unsubscribe();
    this.#workContextPickerSub = null;

    if (this.#workContextPickerRef) {
      this.#workContextPickerRef.close(undefined);
      this.#workContextPickerRef = null;
    }

    this.#isWorkContextPickerOpen.set(false);
  }

  /**
   * Work context picker when >1 option; `confirmSendMode` drives primary button (send vs new chat).
   */
  #openWorkContextPickerDialog(
    options: WorkContextOption[],
    confirmSendMode: boolean,
  ): void {
    if (options.length <= 1) {
      return;
    }

    this.#closeWorkContextPickerIfOpen();

    const ref = this.#workContextPickerDialog.open({
      options,
      confirmSendMode,
    });
    this.#isWorkContextPickerOpen.set(true);
    this.#workContextPickerRef = ref;
    this.#workContextPickerSub = ref
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: unknown) => {
        this.#workContextPickerRef = null;
        this.#workContextPickerSub = null;
        this.#isWorkContextPickerOpen.set(false);

        if (isWorkContextOption(result)) {
          this.confirmWorkContext(result);
        } else {
          this.cancelWorkContextPicker();
        }
      });
  }

  toggleHistorySidebarCollapsed(): void {
    this.#historySidebarCollapsed.update(
      isCollapsed => !isCollapsed,
    );
  }

  openFeedbackForm(id: string): void {
    this.#feedbackMessageId.set(id);
  }

  closeFeedbackForm(): void {
    this.#feedbackMessageId.set('');
  }

  /** Context for v2 API calls: never use a context missing from `allowedContexts` after bootstrap. */
  #currentChatContext(): AiChatContext {
    const bridge = this.#tabBridge;

    if (!bridge) {
      return AI_CHAT_DEFAULT_CONTEXT;
    }

    const preferred = bridge.activeContext();
    const allowed = bridge.allowedContexts();

    if (
      bridge.tabBootstrapComplete()
      && allowed.length > 0
      && !allowed.includes(preferred)
    ) {
      return allowed.includes(AiChatContext.Personal)
        ? AiChatContext.Personal
        : allowed[0];
    }

    return preferred;
  }

  // ─── ChatBot 2.0 methods ─────────────────────────────────────────────────

  /** Fetch the user's chat list. Pass reset=false to append the next page (infinite scroll). */
  loadChats(reset = true): void {
    if (this.#chatsRes.isLoading()) {
      return;
    }

    const page = reset ? 1 : this.#chatsPage();
    const context = this.#tabBridge ? this.#currentChatContext() : undefined;

    if (reset) {
      this.#chatsHasMore.set(false);
    }

    this.#chatsPageReq.set({ page, reset, serial: ++this.#chatsSerial, context });
  }

  /** Load next page of chats (called on scroll). No-op when already loading or no more pages. */
  loadMoreChats(): void {
    if (!this.#chatsHasMore() || this.#chatsRes.isLoading()) {
      return;
    }
    this.loadChats(false);
  }

  /**
   * Select a chat from the history panel — loads its messages.
   * Never touches the send resource: switching chats must not abort a reply
   * the user is waiting on. `#chatId` is set immediately (chatId is a
   * parameter, no need to wait for the GET) so a send started right after
   * switching targets the right chat instead of racing the fetch.
   */
  selectChat(chatId: string): void {
    if (this.#selectedChatId() === chatId) {
      return;
    }

    this.#clearSettledSendError();
    // The held message belongs to the draft being left — it must not go to the selected chat.
    this.#dropHeldSend();

    // A client draft uuid: show its cached turn, but don't promote it to #chatId or fetch it.
    if (this.#isClientDraftKey(chatId)) {
      this.#chatId.set(null);
      this.#draftId.set(chatId);
      this.#selectedChatId.set(chatId);
      this.#selectedChatFetchId.set(undefined);
      this.#draftWorkContext.set(this.#clientDraftKeys.get(chatId) ?? null);

      return;
    }

    // Still generating: the cache already holds its authoritative live state —
    // skip the GET entirely instead of firing one whose result will just be
    // discarded by the apply effect's own inFlightSendKey guard.
    if (this.#inFlightSendKey() === chatId) {
      this.#chatId.set(chatId);
      this.#selectedChatId.set(chatId);
      this.#selectedChatFetchId.set(undefined);

      return;
    }

    this.#chatId.set(chatId);
    this.#selectedChatId.set(chatId);
    this.#selectedChatFetchVersion[chatId] = this.#messagesCacheVersion[chatId] ?? 0;
    this.#selectedChatFetchId.set(chatId);
  }

  /** Rename a chat — optimistic update, reverts on error. */
  renameChat(chatId: string, title: string): void {
    const trimmed = title.trim();
    if (!trimmed || this.#isClientDraftKey(chatId)) {
      return;
    }

    const previous = this.#chats().find(chat => chat.id === chatId);

    this.#renameChatRollback = previous ? { chatId, prevTitle: previous.title } : null;
    // The title is the user's now — drop the generated one so a later list reload
    // cannot resurrect it (notably when they rename back to the default).
    this.#generatedTitles.delete(chatId);
    this.#chats.update(chats => chats.map(chat => chat.id === chatId ? { ...chat, title: trimmed } : chat));
    this.#renameChatReq.set({ chatId, title: trimmed });
  }

  /** Delete a chat — optimistic removal, restores on error. If the deleted chat was selected, clears selection. */
  deleteChat(chatId: string): void {
    if (this.#isClientDraftKey(chatId)) {
      return;
    }

    this.#deleteChatRollback = this.#chats().find(chat => chat.id === chatId) ?? null;

    this.#forgetGeneratedChatTitle(chatId);
    this.#chats.update(chats => chats.filter(chat => chat.id !== chatId));
    this.#dropChatCache(chatId);

    if (this.#selectedChatId() === chatId) {
      this.startNewChat();
    }

    // Show success toast optimistically — error effect will show error toast + restore chat on failure
    this.#toast.show({ type: ToastType.Success, message: this.#translate.instant('chatBot.history.deleteSuccess') });

    this.#deleteChatIdReq.set(chatId);
  }

  /** Pin or unpin a chat — optimistic update, reverts on error. */
  pinChat(chatId: string, isPinned: boolean): void {
    if (this.#isClientDraftKey(chatId)) {
      return;
    }

    const previous = this.#chats().find(chat => chat.id === chatId);

    this.#pinChatRollback = previous ? { chatId, prevIsPinned: previous.isPinned } : null;
    this.#chats.update(chats => sortChats(chats.map(chat => chat.id === chatId ? { ...chat, isPinned } : chat)));
    this.#pinChatReq.set({ chatId, isPinned });
  }

  /**
   * Reset v2 state to start a brand-new conversation (empty chat). Any send
   * still in flight for the chat/draft being left keeps running in the
   * background and is unaffected — a fresh #draftId gives this new draft
   * its own cache slot so it never shows the abandoned draft's messages.
   */
  startNewChat(): void {
    this.#clearSettledSendError();
    this.#chatId.set(null);
    this.#selectedChatId.set(null);
    this.#draftId.set(uuid());
    this.#selectedChatFetchId.set(undefined);
    this.#draftWorkContext.set(null);
    this.#dropHeldSend();
    this.#closeWorkContextPickerIfOpen();
  }

  /**
   * "New chat" action for v2.
   * - For Work with several context options: opens the picker before the draft starts.
   * - For Work with one option: picks it silently.
   * - Otherwise: resets state immediately (same as startNewChat).
   */
  openNewChat(): void {
    this.startNewChat();
    this.#pickDraftWorkContext(this.#workContextOptions(), false);
  }

  /**
   * Picks the draft's For Work option: a single one silently, several via the picker; the pick
   * sends the held message, if any.
   */
  #pickDraftWorkContext(options: WorkContextOption[], confirmSendMode: boolean): void {
    if (options.length === 1) {
      this.confirmWorkContext(options[0]);

      return;
    }

    this.#openWorkContextPickerDialog(options, confirmSendMode);
  }

  /**
   * Options of the active For Work context; empty outside Work or when the user has none.
   * Keys off `activeContext()` like the old `isForWork` check (1:1).
   */
  #workContextOptions(): WorkContextOption[] {
    const bridge = this.#tabBridge;

    return bridge?.activeContext() === AiChatContext.Work ? bridge.workContexts() : [];
  }

  /**
   * Send a message in v2.
   * - For new "For Work" chats with context options: resolves the option first; a picker
   *   dismissed without a choice hands the text back via `onPickerCancel`.
   * - Optimistically prepends the USER message.
   * - On success, updates the chatId, prepends the AGENT reply, refreshes the chat list.
   * - On error, removes the failed user message and surfaces the error.
   */
  sendMessage(
    content: string,
    onError?: () => void,
    onPickerCancel?: (content: string) => void,
  ): void {
    if (
      this.#inFlightSendKey() !== null
      || this.messages().some(message => message.isStreaming === true)
    ) {
      // Not sent, so undo the optimistic CTA cooldown.
      onError?.();

      return;
    }

    const isNewChat = !this.#chatId();
    const options = this.#workContextOptions();

    if (isNewChat && options.length > 0 && !this.#draftWorkContext()) {
      // Held first: the pick sends it, and the picker may close synchronously.
      this.#holdSend(content, onError, onPickerCancel);
      this.#pickDraftWorkContext(options, true);

      return;
    }

    this.#executeSend(content, onError);
  }

  /**
   * Render a canned AGENT reply locally without calling the bot API.
   * Used by static-response CTAs (e.g. the always-on fallback on /personal-info).
   */
  pushStaticReply(content: string): void {
    const now = new Date().toISOString();
    const agentMsg: ChatMessage = {
      id: uuid(),
      chatId: this.#chatId() ?? '',
      role: ChatMessageRole.Agent,
      content,
      // Content is rendered in AI_CHAT_DEFAULT_GREETING_LANG, so tag the message
      // with the matching language rather than a fixed one.
      language: AI_CHAT_DEFAULT_GREETING_LANG.toUpperCase() as ChatLanguage,
      createdAt: now,
      updatedAt: now,
      internalStatus: InternalMessageStatus.resolved,
      // Client-only reply — no server message id, so it cannot accept feedback
      // and a server history snapshot will never contain it.
      isFeedbackEligible: false,
      isClientOnly: true,
    };

    this.#updateMessagesCacheForKey(this.#viewKey(), msgs => [agentMsg, ...msgs]);
  }

  // ─── ChatBot 2.0 feedback ────────────────────────────────────────────────

  /**
   * Toggle a v2 answer's thumbs up/down and persist it via PUT.
   * - Optimistically updates the message, then sends the new rating.
   * - Clearing the selection (reaction = none) drops any submitted review and closes the form.
   * - Switching away from thumbs down also closes an open review form.
   * - On failure the selection is rolled back and the standard error toast is shown.
   */
  toggleReaction(request: ToggleReactionReq): void {
    const previous = this.messages().find(message => message.id === request.id);

    if (!previous) {
      return;
    }

    const rollback = {
      reactionType: previous.reactionType,
      isFeedbackProvided: previous.isFeedbackProvided,
    };
    const isCleared = request.reaction === ChatMessageReaction.none;

    this.#patchMessage(request.id, {
      reactionType: request.reaction,
      isFeedbackProvided: isCleared ? false : previous.isFeedbackProvided,
    });

    if (
      request.reaction !== ChatMessageReaction.dislike
      && this.#feedbackMessageId() === request.id
    ) {
      this.closeFeedbackForm();
    }

    this.#submitFeedback(request.id, { rating: this.#reactionToRating(request.reaction) }, () =>
      this.#patchMessage(request.id, rollback),
    );
  }

  /**
   * Submit the v2 "Leave a review" form for a thumbs-down answer.
   * Optimistically marks the answer as feedback-provided, then persists rating + reason + comment.
   * Rolls back and shows the standard error on failure.
   */
  leaveFeedback(request: LeaveFeedbackReq): void {
    const previous = this.messages().find(message => message.id === request.id);

    if (!previous) {
      return;
    }

    const wasFeedbackProvided = previous.isFeedbackProvided;
    const comment = request.comment.trim();

    this.#patchMessage(request.id, { isFeedbackProvided: true });

    this.#submitFeedback(
      request.id,
      {
        rating: MessageRating.notHelpful,
        reason: request.reason,
        ...(comment !== '' ? { comment } : {}),
      },
      () => this.#patchMessage(request.id, { isFeedbackProvided: wasFeedbackProvided }),
    );
  }

  /** Map a thumbs up/down selection to the v2 API rating (`null` clears feedback). */
  #reactionToRating(reaction: ChatMessageReaction): MessageRating | null {
    if (reaction === ChatMessageReaction.like) {
      return MessageRating.helpful;
    }

    if (reaction === ChatMessageReaction.dislike) {
      return MessageRating.notHelpful;
    }

    return null;
  }

  #submitFeedback(
    messageId: string,
    body: SubmitFeedbackReq,
    onError: () => void,
  ): void {
    this.#service
      .submitFeedback(messageId, body)
      .pipe(
        tapResponse({
          next: () => undefined,
          error: () => {
            onError();
            this.#toast.show({
              type: ToastType.Error,
              message: this.#translate.instant('chatBot.error.text'),
            });
          },
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  /** True while `chatId` is the key of an optimistic row whose chat the server has not created yet. */
  #isClientDraftKey(chatId: string): boolean {
    return this.#clientDraftKeys.has(chatId);
  }

  /** Drops a settled send's stale error so it doesn't follow the user into another chat. */
  #clearSettledSendError(): void {
    if (this.#inFlightSendKey() !== null || !this.#sendMessageRes.error()) {
      return;
    }

    this.#sendMessageReq.set(undefined);
  }

  /** Releases the global send lock once its terminal result lands, by originKey so this survives the chat being deleted mid-stream. */
  #settleSend(req: SendMessageRequest): void {
    this.#clientDraftKeys.delete(req.originKey);

    if (this.#inFlightSendOrigin !== req.originKey) {
      return;
    }

    this.#inFlightSendOrigin = null;
    this.#inFlightSendKey.set(null);
  }

  /** On a full list reset, keep the sidebar row of the send still in flight, which the server list cannot know about yet. */
  #mergeResetChatsList(
    existing: ChatListItem[],
    serverItems: ChatListItem[],
  ): ChatListItem[] {
    const serverIds = new Set(serverItems.map(item => item.id));
    const inFlightKey = this.#inFlightSendKey();
    // Only the live send's row is force-kept — anything else the server omits stays omitted.
    const localOnly = existing.filter(
      item => !serverIds.has(item.id) && item.id === inFlightKey,
    );

    return [
      ...new Map(
        [...localOnly, ...serverItems.map(item => this.#withGeneratedTitle(item))].map(item => [item.id, item]),
      ).values(),
    ];
  }

  #requestGeneratedChatTitle(chatId: string, content: string): void {
    if (!chatId || !content || this.#titleRequestedChatIds.has(chatId)) {
      return;
    }

    const current = this.#chats().find(chat => chat.id === chatId);

    if (current && current.title !== DEFAULT_CHAT_TITLE) {
      return;
    }

    this.#titleRequestedChatIds.add(chatId);
    this.#service
      .generateChatTitle(chatId, content)
      .pipe(
        tapResponse({
          next: updated => {
            if (!updated || updated.title === DEFAULT_CHAT_TITLE) {
              this.#titleRequestedChatIds.delete(chatId);

              return;
            }

            this.#applyGeneratedChatTitle(updated);
          },
          error: () => {
            this.#titleRequestedChatIds.delete(chatId);
          },
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #applyGeneratedChatTitle(updated: ChatListItem): void {
    const current = this.#chats().find(chat => chat.id === updated.id);

    if (current && current.title !== DEFAULT_CHAT_TITLE) {
      return;
    }

    if (updated.title !== DEFAULT_CHAT_TITLE) {
      this.#generatedTitles.set(updated.id, updated.title);
    }

    this.#chats.update(chats => {
      const next = this.#withGeneratedTitle(updated);
      const exists = chats.some(chat => chat.id === next.id);

      if (!exists) {
        return chats;
      }

      return sortChats(
        chats.map(chat => (chat.id === next.id ? { ...chat, title: next.title } : chat)),
      );
    });
  }

  #withGeneratedTitle(chat: ChatListItem): ChatListItem {
    const generated = this.#generatedTitles.get(chat.id);

    if (!generated || chat.title !== DEFAULT_CHAT_TITLE) {
      return chat;
    }

    return { ...chat, title: generated };
  }

  /** Drops every trace of a chat's generated title so the maps cannot outlive the chat. */
  #forgetGeneratedChatTitle(chatId: string): void {
    this.#titleRequestedChatIds.delete(chatId);
    this.#generatedTitles.delete(chatId);
  }

  /** Drop cached messages for a deleted chat and its draft origin. Leaves #resolvedSendKeys alone — #sendKeyFor still needs it to route a mid-stream error for this chat. */
  #dropChatCache(chatId: string): void {
    const originKeys = Object.keys(this.#resolvedSendKeys)
      .filter(origin => this.#resolvedSendKeys[origin] === chatId);

    const keysToDrop = new Set([chatId, ...originKeys]);

    this.#messagesCache.update(cache => {
      const next = { ...cache };

      for (const key of keysToDrop) {
        delete next[key];
        delete this.#messagesCacheVersion[key];
        delete this.#selectedChatFetchVersion[key];
      }

      return next;
    });
  }

  #setMessagesCacheForKey(key: string, msgs: ChatMessage[]): void {
    this.#messagesCache.update(cache => ({ ...cache, [key]: msgs }));
    this.#messagesCacheVersion[key] = (this.#messagesCacheVersion[key] ?? 0) + 1;
  }

  #updateMessagesCacheForKey(key: string, updater: (msgs: ChatMessage[]) => ChatMessage[]): void {
    this.#messagesCache.update(cache => ({ ...cache, [key]: updater(cache[key] ?? []) }));
    this.#messagesCacheVersion[key] = (this.#messagesCacheVersion[key] ?? 0) + 1;
  }

  /** Moves a draft's cached messages under its real chatId once the server assigns one. */
  #renameMessagesCacheKey(fromKey: string, toKey: string): void {
    if (fromKey === toKey) {
      return;
    }

    this.#messagesCache.update(cache => {
      if (!(fromKey in cache)) {
        return cache;
      }

      const { [fromKey]: moved, ...rest } = cache;

      return { ...rest, [toKey]: moved };
    });

    if (fromKey in this.#messagesCacheVersion) {
      this.#messagesCacheVersion[toKey] = this.#messagesCacheVersion[fromKey];
      delete this.#messagesCacheVersion[fromKey];
    }
  }

  /**
   * A chatId shows up in the response once the server has created the chat
   * (first message of a new session). Moves the draft's cached messages
   * under that chatId, and — only if the user has not since navigated away
   * from the draft/chat this send started in — follows the switch so they
   * keep seeing their own conversation instead of being left behind on an
   * id nothing will ever select again.
   */
  #resolveSendKey(req: SendMessageRequest, agentMsg: ChatMessage): string {
    const known = this.#resolvedSendKeys[req.originKey];

    if (known) {
      return known;
    }

    if (!agentMsg.chatId || agentMsg.chatId === req.originKey) {
      return req.originKey;
    }

    this.#renameMessagesCacheKey(req.originKey, agentMsg.chatId);
    this.#resolvedSendKeys[req.originKey] = agentMsg.chatId;
    this.#clientDraftKeys.delete(req.originKey);

    if (this.#inFlightSendOrigin === req.originKey) {
      this.#inFlightSendKey.set(agentMsg.chatId);
    }

    if (this.#viewKey() === req.originKey) {
      this.#chatId.set(agentMsg.chatId);
      this.#selectedChatId.set(agentMsg.chatId);
    }

    // Swap the optimistic sidebar entry's id for the real one, in place — no
    // list reload/flash. The entry already exists (added in #executeSend),
    // so a chat left mid-stream still has a list item to switch back to.
    this.#chats.update(chats => chats.map(chat => chat.id === req.originKey
      ? { ...chat, id: agentMsg.chatId }
      : chat));

    return agentMsg.chatId;
  }

  /** Cache key a send's messages currently live under — the resolved chatId once known, otherwise its origin key. */
  #sendKeyFor(req: SendMessageRequest): string {
    return this.#resolvedSendKeys[req.originKey] ?? req.originKey;
  }

  #patchMessage(id: string, changes: Partial<ChatMessage>): void {
    this.#updateMessagesCacheForKey(this.#viewKey(), messages => messages.map(
      message => (message.id === id ? { ...message, ...changes } : message),
    ));
  }

  /**
   * Holds the message until the pick; one it replaces is dropped, so its CTA
   * cooldown is undone.
   */
  #holdSend(
    content: string,
    onError?: () => void,
    onPickerCancel?: (content: string) => void,
  ): void {
    this.#pendingOnError?.();
    this.#pendingContent = content;
    this.#pendingOnError = onError;
    this.#pendingOnPickerCancel = onPickerCancel;
  }

  /**
   * Drops the held message; it was not sent, so its CTA cooldown is undone.
   */
  #dropHeldSend(): void {
    this.#pendingContent = null;
    this.#pendingOnPickerCancel = undefined;
    this.#pendingOnError?.();
    this.#pendingOnError = undefined;
  }

  /** Applies the picked For Work option to the draft chat and sends the held message, if any. */
  confirmWorkContext(option: WorkContextOption): void {
    this.#draftWorkContext.set(option);
    const content = this.#pendingContent;
    const onError = this.#pendingOnError;
    this.#pendingContent = null;
    this.#pendingOnError = undefined;
    this.#pendingOnPickerCancel = undefined;

    if (content) {
      this.#executeSend(content, onError);
    }
  }

  /**
   * Drops the held message after the picker was dismissed without a choice; its text goes back to
   * the sender.
   */
  cancelWorkContextPicker(): void {
    const content = this.#pendingContent;
    const onPickerCancel = this.#pendingOnPickerCancel;
    this.#dropHeldSend();

    if (content) {
      onPickerCancel?.(content);
    }
  }

  #applyStreamingAgentSnapshot(
    req: SendMessageRequest,
    agentMsg: ChatMessage,
  ): void {
    const key = this.#resolveSendKey(req, agentMsg);

    this.#updateMessagesCacheForKey(key, msgs => msgs.map(message => {
      if (message.id === req.pendingAgentId) {
        return {
          ...message,
          chatId: agentMsg.chatId || message.chatId,
          content: agentMsg.content,
          internalStatus: agentMsg.content
            ? InternalMessageStatus.resolved
            : InternalMessageStatus.pending,
          isStreaming: true,
          isFeedbackEligible: false,
        };
      }

      if (message.id === req.optimisticId && agentMsg.chatId) {
        return { ...message, chatId: agentMsg.chatId };
      }

      return message;
    }));
  }

  #applyFinalAgentReply(
    req: SendMessageRequest,
    agentMsg: ChatMessage,
  ): void {
    // Reply received: the send succeeded, so the cooldown stays. Clear the
    // rollback so closing the chat afterwards does not undo it.
    this.#inFlightSendOnError = null;

    const chatId = agentMsg.chatId;
    const wasNewChat = req.req.chatId === null;
    const key = this.#resolveSendKey(req, agentMsg);

    this.#settleSend(req);
    this.#updateMessagesCacheForKey(key, msgs => [
      { ...agentMsg, isStreaming: false },
      ...msgs
        .filter(message => message.id !== req.pendingAgentId)
        .map(message => message.id === req.optimisticId
          ? { ...message, chatId, internalStatus: InternalMessageStatus.resolved }
          : message),
    ]);

    // Only now: on the streaming path the server holds the chat row in an open
    // transaction for the whole stream, so a title POST fired at `meta` 404s.
    if (chatId) {
      this.#requestGeneratedChatTitle(chatId, req.req.content);
    }

    if (!wasNewChat || !chatId) {
      return;
    }

    // First message: the sidebar already has an optimistic entry (added in
    // #executeSend, renamed to the real id in #resolveSendKey) — patch its
    // title/metadata in place instead of reloading the whole list.
    this.#refreshChatListItemMeta(chatId);
  }

  /** Patches one sidebar entry's title/metadata from the server, in place — no full-list reload/flash. */
  #refreshChatListItemMeta(chatId: string): void {
    // A 404 right after creation is replication lag, not a missing chat — worth retrying.
    this.#service.getChat(chatId, { retryOnNotFound: true })
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: chat => {
          if (!chat) {
            return;
          }

          this.#chats.update(chats => sortChats(chats.map(item => item.id === chatId
            ? {
              ...item,
              userEmail: chat.userEmail,
              title: chat.title,
              isPinned: chat.isPinned,
              updatedAt: chat.updatedAt,
              context: chat.context,
              license: chat.license,
              workScope: chat.workScope,
              // Keeps the picked option's label if this response omits it.
              workContextLabel: chat.workContextLabel ?? item.workContextLabel,
            }
            : item)));
        },
        // Best-effort metadata patch — on failure the entry just keeps its
        // placeholder title until the next list reload; not worth an error.
        error: () => undefined,
      });
  }

  #shouldKeepPartialOnStreamError(
    req: SendMessageRequest,
    streamError: unknown,
  ): boolean {
    if (streamError instanceof ChatStreamFailure && streamError.hadContent) {
      return true;
    }

    const key = this.#sendKeyFor(req);

    return (this.#messagesCache()[key] ?? []).some(
      message => message.id === req.pendingAgentId && Boolean(message.content),
    );
  }

  #removeOptimisticSendMessages(req: SendMessageRequest): void {
    const key = this.#sendKeyFor(req);

    this.#updateMessagesCacheForKey(key, msgs => msgs.filter(
      message => message.id !== req.optimisticId && message.id !== req.pendingAgentId,
    ));
  }

  #keepPartialStreamOnError(req: SendMessageRequest): void {
    const key = this.#sendKeyFor(req);

    this.#updateMessagesCacheForKey(key, msgs => msgs.map(message => {
      if (message.id === req.pendingAgentId) {
        return {
          ...message,
          isStreaming: false,
          isFeedbackEligible: false,
          internalStatus: message.content
            ? InternalMessageStatus.resolved
            : InternalMessageStatus.failed,
        };
      }

      if (message.id === req.optimisticId) {
        return {
          ...message,
          internalStatus: InternalMessageStatus.resolved,
        };
      }

      return message;
    }));
  }

  #executeSend(content: string, onError?: () => void): void {
    const optimisticId = uuid();
    const pendingAgentId = uuid();
    const now = new Date().toISOString();
    const originKey = this.#viewKey();
    const userMsg: ChatMessage = {
      id: optimisticId,
      chatId: this.#chatId() ?? '',
      role: ChatMessageRole.User,
      content,
      language: ChatLanguage.Ru,
      createdAt: now,
      updatedAt: now,
      internalStatus: InternalMessageStatus.pending,
      // Persisted server-side under its own id, never under this client uuid.
      isClientOnly: true,
    };
    // Optimistic AGENT placeholder so the chat shows the typing-dots indicator
    // (bot-chat-message renders dots for a pending AGENT message) while the reply loads.
    const pendingAgentMsg: ChatMessage = {
      id: pendingAgentId,
      chatId: this.#chatId() ?? '',
      role: ChatMessageRole.Agent,
      content: '',
      language: AI_CHAT_DEFAULT_GREETING_LANG.toUpperCase() as ChatLanguage,
      createdAt: now,
      updatedAt: now,
      internalStatus: InternalMessageStatus.pending,
      isStreaming: true,
      isFeedbackEligible: false,
      isClientOnly: true,
    };

    const isNewChat = !this.#chatId();
    const context = isNewChat && this.#tabBridge ? this.#currentChatContext() : undefined;
    // Only a new Work chat carries an option — anything else is a 422 on the backend.
    const workContext = isNewChat && context === AiChatContext.Work
      ? (this.#draftWorkContext() ?? undefined)
      : undefined;

    if (isNewChat) {
      this.#clientDraftKeys.set(originKey, workContext ?? null);
      // Select it now so the synthetic "New chat" row doesn't render alongside it.
      this.#selectedChatId.set(originKey);
      // Optimistic sidebar entry so the new chat is switchable to immediately — no
      // waiting on a server round trip. `#resolveSendKey`/`#refreshChatListItemMeta`
      // fill in the real id/title once the server responds.
      this.#chats.update(chats => sortChats([
        {
          id: originKey,
          userEmail: '',
          title: DEFAULT_CHAT_TITLE,
          isPinned: false,
          createdAt: now,
          updatedAt: now,
          ...(context != null ? { context } : {}),
          ...workContextChatFields(workContext),
        },
        ...chats,
      ]));
    }

    const sendReq: SendMessageRequest = {
      req: {
        chatId: this.#chatId(),
        content,
        ...(context != null ? { context } : {}),
        ...(workContext != null ? { workContext: workContextRequest(workContext) } : {}),
      },
      optimisticId,
      pendingAgentId,
      originKey,
      onError,
    };

    this.#inFlightSendOnError = onError ?? null;
    this.#updateMessagesCacheForKey(originKey, msgs => [pendingAgentMsg, userMsg, ...msgs]);
    // Sends are globally serialised — the previous one's mapping is stale now.
    this.#resolvedSendKeys = {};
    this.#sendMessageReq.set(sendReq);
    this.#inFlightSendKey.set(originKey);
    this.#inFlightSendOrigin = originKey;

    // Like v1: swap the typing dots for the "please wait" message if the reply is
    // still pending after the timeout. No-op once the placeholder is removed.
    timer(AI_CHAT_SEND_PENDING_TIMEOUT_MS)
      .pipe(
        tap(() => {
          // Already settled — the placeholder this would patch is gone.
          if (this.#inFlightSendOrigin !== originKey) {
            return;
          }

          this.#updateMessagesCacheForKey(this.#sendKeyFor(sendReq), msgs => msgs.map(message => message.id === pendingAgentId
              && message.internalStatus === InternalMessageStatus.pending
            ? { ...message, internalStatus: InternalMessageStatus.timeout }
            : message));
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  // ─────────────────────────────────────────────────────────────────────────
}

/** In-flight v2 send: API payload + optimistic message ids for stream/final/error updates. */
type SendMessageRequest = {
  req: CreateChatMessageReq;
  optimisticId: string;
  /** Id of the optimistic pending AGENT placeholder shown as the typing indicator. */
  pendingAgentId: string;
  /** Cache key (chatId, or the draft id) this send was made from — where its reply/error is applied even if the user has since switched away. */
  originKey: string;
  /** Optional callback invoked if this send fails. */
  onError?: () => void;
};

/** Sort chats: pinned chats first, then by most recent activity (updatedAt desc). */
function sortChats(chats: ChatListItem[]): ChatListItem[] {
  return [...chats].sort((a, b) => {
    if (a.isPinned !== b.isPinned) {
      return a.isPinned ? -1 : 1;
    }

    return b.updatedAt.localeCompare(a.updatedAt);
  });
}
